import { css } from '@/styled-system/css';

const MOBILE_HEADER_HEIGHT = '48px';

export const detailHubContainer = css({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'hpxWhite',
  position: 'relative',
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': {
    minHeight: '100dvh',
  },
  md: {
    minHeight: 'unset',
    height: '100%',
    overflow: 'hidden',
  },
});

export const detailHeader = css({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: '1x',
  py: '1x',
  minHeight: MOBILE_HEADER_HEIGHT,
  zIndex: 'zIndexOverParent',
  willChange: 'transform',
  transitionProperty: 'background-color, border-bottom-color',
  transitionDuration: '0.3s',
  transitionTimingFunction: 'ease-out',
  md: {
    display: 'none',
  },
});

export const headerActions = css({
  display: 'flex',
  gap: '1x',
  alignItems: 'center',
});

export const detailMainContent = css({
  flex: 1,
  position: 'relative',
  md: {
    overflow: 'auto',
  },
});

export const detailMainContentPadding = css({
  paddingLeft: '2x',
  paddingRight: '2x',
  md: {
    paddingLeft: '4x',
    paddingRight: '4x',
  },
});

export const photoGalleryPadding = css({
  md: {
    px: '4x',
  },
});

// Holds the rental-type badges and the trust signal "Updated · Listed"
// text. The wrapping <div> in HaloListingPage carries `data-summary-row=""`
// so this style block owns one named coupling instead of relying on
// tag-shape alone.
//
// Layout rules:
// - No badges: row stays a single flex row with `justify-content: flex-end`
//   so the lone trust signal pins to the right edge (mobile and desktop).
// - With badges (`<ul data-rental-type-list>` present):
//   - Mobile: switch to `flex-direction: column` so badges sit on row 1
//     and the trust signal sits on row 2, each left-aligned.
//   - Desktop (md+): stay in `flex-direction: row`; the badges'
//     `marginRight: auto` pushes the trust signal to the right of the
//     same line.
//
// Sibling-padding collapse: the row's marginBottom plus the next module's
// top padding stack visually on mobile. Zero out the next module's first
// <article>'s padding on mobile so the gap from the row to the price
// stays exactly 1x. The selector chains two structural hooks
// (`[data-summary-row]` on the row, `[data-module-list]` on
// `ModuleRenderer`'s wrapper) plus the `<article>` rendered by `ui/Module`;
// if `Module` stops rendering its content inside an `<article>`, this is
// the place to update.
export const summaryRow = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: '2x',
  marginTop: '1x',
  marginBottom: '1x',
  '&:has(> [data-rental-type-list])': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: '1x',
  },
  '&[data-summary-row] + [data-module-list] > article:first-of-type': {
    paddingTop: 0,
  },
  md: {
    marginBottom: 0,
    '&:has(> [data-rental-type-list])': {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '2x',
    },
    '&[data-summary-row] + [data-module-list] > article:first-of-type': {
      paddingTop: '4x',
    },
  },
});

export const listingTitle = css({
  fontSize: 'lg',
  fontWeight: 'bold',
  color: 'hpxBlue600',
  margin: 0,
  paddingX: '2x',
  paddingTop: `calc(${MOBILE_HEADER_HEIGHT} + token(spacing.2x))`,
  paddingBottom: '2x',
  md: {
    fontSize: 'xl',
    paddingX: '4x',
    paddingTop: '2x',
  },
});

export const expandedCloseButton = css({
  position: 'static',
  top: 'unset',
  left: 'unset',
});

export const detailFooter = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1x',
  padding: '2x',
  backgroundColor: 'hpxWhite',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'hpxGrey400',
  minHeight: '72px',
  position: 'sticky',
  bottom: 0,
  zIndex: 'zIndexStep',
  md: {
    display: 'none',
  },
});

export const spokeFooterActions = css({
  display: 'flex',
  gap: '1x',
  width: '100%',
  maxWidth: '400px',
});
